import {
  jsonSchema,
  type ModelMessage,
  isStepCount,
  streamText,
  toUIMessageStream,
  tool,
  type UIMessageStreamWriter,
} from 'ai';

import { type Subagent } from './mcpClient';
import { dixaGateway } from './provider';
import { type MimUIMessage } from './structuredResponse';
import { toTraceparent } from './traceparent';

type SubagentInput = {
  query: string;
};

/** stopWhen defaults to 1,
 *  which means that it stops immediately after processing the strucuted output.
 *  Set to 10 so that we have some "budget" to call tools.
 *  See https://ai-sdk.dev/docs/reference/ai-sdk-core/stream-text#stop-when
 */
const STEP_COUNT = 10;

const inputSchema = jsonSchema<SubagentInput>({
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: 'Plain-English question for the subagent to answer.',
    },
  },
  required: ['query'],
});

export type SubagentHistories = Map<string, ModelMessage[]>;

export const createSubagentTool = (
  subagent: Subagent,
  chatId: string,
  writer: UIMessageStreamWriter<MimUIMessage>,
  histories: SubagentHistories,
) =>
  tool<SubagentInput, string, Record<string, unknown>>({
    description: subagent.description,
    inputSchema,
    metadata: { stateCopy: subagent.stateCopy ?? {} },
    execute: async ({ query }, { abortSignal }) => {
      const prior = histories.get(subagent.name) ?? [];
      const messages: ModelMessage[] = [
        ...prior,
        { role: 'user', content: query },
      ];
      const result = streamText({
        model: dixaGateway(subagent.prompt),
        messages,
        tools: subagent.tools,
        abortSignal,
        headers: { traceparent: toTraceparent(chatId) },
        stopWhen: isStepCount(STEP_COUNT),
      });

      const boundaryId = `${subagent.name}-${crypto.randomUUID()}`;
      writer.write({
        type: 'data-subagent-open',
        id: boundaryId,
        data: subagent.name,
      });
      writer.merge(
        toUIMessageStream({
          stream: result.stream,
          sendStart: false,
          sendFinish: false,
        }),
      );

      const text = await result.text;
      const responseMessages = await result.responseMessages;
      histories.set(subagent.name, [...messages, ...responseMessages]);
      writer.write({
        type: 'data-subagent-close',
        id: boundaryId,
        data: subagent.name,
      });
      return text;
    },
  });
